import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@ota/api-client';
import { authStore, canReviewProperty, fetchReviews, postReview } from '../api';
import { formatDate } from '@ota/design-system';

interface ReviewsSectionProps {
  propertyId: number;
  avgRating?: number;
  reviewCount: number;
}

export function ReviewsSection({ propertyId, avgRating, reviewCount }: ReviewsSectionProps) {
  const session = authStore.get();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rating, setRating] = useState<number | null>(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');

  const reviews = useQuery({
    queryKey: ['reviews', propertyId],
    queryFn: () => fetchReviews(propertyId, 0, 20),
  });

  const eligibility = useQuery({
    queryKey: ['can-review', propertyId, session?.user.id],
    queryFn: () => canReviewProperty(propertyId),
    enabled: !!session,
  });

  const submit = useMutation({
    mutationFn: () => postReview(propertyId, {
      rating: rating ?? 5,
      title: title || undefined,
      comment: comment || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['property', propertyId] });
      queryClient.invalidateQueries({ queryKey: ['can-review', propertyId] });
      setDialogOpen(false);
      setRating(5);
      setTitle('');
      setComment('');
    },
  });

  const summaryColor = avgRating
    ? avgRating >= 4.5 ? '#00A859' : avgRating >= 3.5 ? '#1F8DD6' : avgRating >= 2.5 ? '#F2A93B' : '#E74C3C'
    : '#1F8DD6';

  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack>
            <Typography variant="h5" sx={{ mb: 0.5 }}>Guest reviews</Typography>
            {reviewCount > 0 && avgRating !== undefined ? (
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  sx={{
                    px: 1.25, py: 0.5,
                    borderRadius: 1,
                    bgcolor: summaryColor,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '1rem',
                  }}
                >
                  {avgRating.toFixed(1)}
                </Box>
                <Stack>
                  <Rating value={avgRating} precision={0.1} readOnly size="small" />
                  <Typography variant="caption" color="text.secondary">
                    Based on {reviewCount} review{reviewCount === 1 ? '' : 's'}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No reviews yet — be the first to share your experience.
              </Typography>
            )}
          </Stack>

          {/* Eligibility-aware CTA */}
          {!session && (
            <Button
              component={Link}
              to={`/login?returnTo=${encodeURIComponent(`/hotels/${propertyId}`)}`}
              variant="outlined"
              startIcon={<RateReviewIcon />}
            >
              Sign in to write a review
            </Button>
          )}
          {session && eligibility.data && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RateReviewIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Write a review
            </Button>
          )}
          {session && eligibility.data === false && (
            <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 300, textAlign: { md: 'right' } }}>
              You can review this property after you've stayed here.
            </Typography>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {reviews.isLoading && <CircularProgress size={24} />}
        {reviews.data && reviews.data.content.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No reviews yet.
          </Typography>
        )}

        <Stack spacing={2}>
          {(reviews.data?.content ?? []).map((r) => (
            <Box key={r.id} sx={{ p: 2, borderRadius: 2, bgcolor: 'background.subtle' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14 }}>
                  {r.guestName.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
                </Avatar>
                <Stack flex={1}>
                  <Typography variant="subtitle2">{r.guestName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(r.createdAt)}
                    {r.source === 'GOOGLE' && ' · via Google'}
                  </Typography>
                </Stack>
                <Rating value={Number(r.rating)} precision={0.5} readOnly size="small" />
              </Stack>
              {r.title && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {r.title}
                </Typography>
              )}
              {r.comment && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {r.comment}
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      </CardContent>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Write a review</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Stack alignItems="center">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                How was your stay?
              </Typography>
              <Rating
                value={rating}
                precision={0.5}
                size="large"
                onChange={(_, v) => setRating(v)}
              />
            </Stack>
            <TextField
              label="Title (optional)"
              fullWidth
              placeholder="Sum it up in one line"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              label="Your review"
              fullWidth
              multiline
              minRows={4}
              placeholder="Tell future travelers what made your stay memorable…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            {submit.isError && (
              <Alert severity="error">
                {submit.error instanceof ApiError ? submit.error.body?.message ?? submit.error.message : 'Failed to post review'}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={submit.isPending || !rating}
            onClick={() => submit.mutate()}
          >
            {submit.isPending ? 'Posting…' : 'Post review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
